import React, { useContext, useState, useEffect, useLayoutEffect, useRef } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Capacitor } from "@capacitor/core";
import { AuthContext } from "../context/AuthContext";
import { ordersApi } from "../services/orders";
import { isCustomerPage } from "../lib/customerPages";
import FanProfileSheet from "./FanProfileSheet";
import FanReferEarnSheet from "./FanReferEarnSheet";
import FanProfileEditorSheet from "./FanProfileEditorSheet";
import FanSignInSheet from "./FanSignInSheet";
import FanAvatar from "./FanAvatar";
import {
  FAN_PROFILE_EVENT,
  POINTS_PER_DEVICE_REQUEST,
  hasFanNew,
  listFanOrders,
} from "../lib/fanProfile";
import {
  FAN_ACCOUNT_EVENT,
  ensureFanSession,
  ensureFanPointsFresh,
  getFanPoints,
  getFanUser,
  isFanSignedIn,
  needsFirstTimeScreens,
  FanUser,
} from "../lib/fanAccount";
import "./Header.css";
import { t as tr } from "../i18n";

// The mobile hamburger menu was removed in the mobile redesign — the bottom
// tab bar covers everything it used to hold, so on phones this header is just
// the wordmark. The Live/Offline pill is NOT here: it floats in the top-right
// corner of the app pages (see LiveStatusFloat). Desktop keeps its own top nav.
const Header: React.FC = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated, logout } = useContext(AuthContext);
  const location = useLocation();
  const headerRef = useRef<HTMLElement>(null);
  const logoRef = useRef<HTMLImageElement>(null);
  const topSongsRef = useRef<HTMLAnchorElement>(null);

  const [pendingCount, setPendingCount] = useState<number>(0);

  // Header is position:fixed (see Header.css) so it's anchored to the real
  // screen and can't be scrolled/panned out of view when the keyboard opens —
  // but that also takes it out of document flow, so Layout's <main> needs to
  // reserve the same amount of space itself via padding-top. Measuring the
  // live DOM node keeps the two in sync across breakpoints and safe-area
  // updates: any change to the header's rendered height re-fires this observer.
  useEffect(() => {
    const el = headerRef.current;
    if (!el) return;
    const measure = () => {
      document.documentElement.style.setProperty(
        "--app-header-h",
        `${Math.round(el.getBoundingClientRect().height)}px`
      );
    };
    // Measure ONCE up front, then observe — the same order BottomTabBar uses
    // for --app-tabbar-h, and the half this one was missing (a P0 fix).
    // A ResizeObserver's first callback is delivered on a rendering frame, and
    // a document that mounts while hidden (iOS launching the app into the
    // background, an OS prewarm, the founder switching apps during the splash)
    // gets no frames — so the callback never arrived, and because the header
    // never changes size afterwards it never arrived LATER either. The var
    // stayed unset for the whole session, every `var(--app-header-h, 0px)` in
    // the app fell back to 0, <main> reserved nothing for the fixed header, and
    // the page rendered underneath it: the DJ name row and PENDING REQUESTS
    // heading half-swallowed by the green bar (founder, native 1.1(34)).
    // getBoundingClientRect is exact on a hidden document, so this always wins.
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // Web only. Mobile Safari keeps the layout viewport full-height while the
  // keyboard is up and PANS the visual viewport instead, and position:fixed
  // is fixed to the LAYOUT viewport — so the moment a fan scrolls with the
  // keyboard open (the song-search rise does exactly that) the green bar
  // slides off the top of the screen. Following the visual viewport's
  // offset keeps it on screen; the transform is cleared the instant the
  // offset is 0 again, so normal pages never carry one. The native app has
  // no pan: the Keyboard plugin resizes the webview (capacitor.config.ts).
  useEffect(() => {
    const el = headerRef.current;
    const vv = window.visualViewport;
    if (!el || !vv || Capacitor.isNativePlatform()) return;
    const follow = () => {
      const y = Math.round(vv.offsetTop);
      el.style.transform = y > 0 ? `translateY(${y}px)` : "";
    };
    vv.addEventListener("scroll", follow);
    vv.addEventListener("resize", follow);
    return () => {
      vv.removeEventListener("scroll", follow);
      vv.removeEventListener("resize", follow);
      el.style.transform = "";
    };
  }, []);

  // Signed-out "Top Songs" (1.5.2, founder pick A): two plain links of one
  // size and weight (Top Songs, Log in) + the Sign Up pill. The words always
  // keep >= 12px of air after the wordmark, nothing is squashed, and nothing
  // runs off the right edge, in every language on every phone. English fits
  // whole from 320px (Header.css). A longer language steps down only where it
  // must: the white trophy alone (still a 44px tap target, named by its
  // aria-label), then out of the header (the footer and the homepage strip
  // still link the board). Measured on the real rendered links, never guessed.
  useLayoutEffect(() => {
    const link = topSongsRef.current;
    const logo = logoRef.current;
    const header = headerRef.current;
    if (!link || !logo || !header) return;
    const nav = link.parentElement!;
    const fit = () => {
      for (const mode of ["full", "icon", "none"] as const) {
        link.dataset.fit = mode;
        if (mode === "none") return;
        const room = header.getBoundingClientRect().right - parseFloat(getComputedStyle(header).paddingRight);
        const items = Array.from(nav.children) as HTMLElement[];
        const first = Math.min(...items.map((el) => el.getBoundingClientRect().left));
        const whole = items.every((el) => el.scrollWidth <= el.clientWidth + 1);
        if (whole && first - logo.getBoundingClientRect().right >= 12 && nav.getBoundingClientRect().right <= room + 0.5) return;
      }
    };
    fit();
    // The wordmark's width is only known once it has loaded.
    logo.addEventListener("load", fit);
    window.addEventListener("resize", fit);
    document.fonts?.ready.then(fit).catch(() => {});
    return () => {
      logo.removeEventListener("load", fit);
      window.removeEventListener("resize", fit);
    };
  });

  // Pending-request count for the desktop nav's Requests badge.
  useEffect(() => {
    const fetchCount = async () => {
      if (!isAuthenticated) return;
      try {
        const data = await ordersApi.newRequests();
        setPendingCount(data.length);
      } catch (err) {
        console.error("Error fetching new orders", err);
      }
    };
    fetchCount();
    const t = setInterval(fetchCount, 30_000);
    return () => clearInterval(t);
  }, [isAuthenticated]);

  const handleLogout = () => {
    logout();
    navigate("/");
  };

  // Fans on a talent's page (QR scan, /dj/:slug, or the /:slug short link)
  // shouldn't see Login / Sign Up or the talent nav — just the wordmark and
  // the fan profile chip (Phase 2). The 🏆 Leaderboard entry that used to sit
  // here moved INSIDE the profile sheet as its prominent top button.
  const hideAllLinks = isCustomerPage(location.pathname);
  const [profileOpen, setProfileOpen] = useState(false);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editorVerify, setEditorVerify] = useState(false);
  const [referOpen, setReferOpen] = useState(false);
  const [signInOpen, setSignInOpen] = useState(false);
  // Someone new lands on their profile to set it up, never on an empty "No
  // requests yet" (founder, 2026-11-03). A fan coming back stays where they were.
  const afterSignIn = (fan: FanUser) => {
    if (!needsFirstTimeScreens(fan)) return;
    setProfileOpen(false);
    setEditorOpen(true);
  };

  // The chip reads localStorage (name, "something resolved" dot) and the fan
  // account store — re-render whenever either changes, including from other
  // components (nickname modal, editor sheet, sign-in) or another tab.
  const [, setProfileTick] = useState(0);
  useEffect(() => {
    if (!hideAllLinks) return;
    const bump = () => setProfileTick((t) => t + 1);
    window.addEventListener(FAN_PROFILE_EVENT, bump);
    window.addEventListener(FAN_ACCOUNT_EVENT, bump);
    window.addEventListener("storage", bump);
    // Refresh-on-boot (fan accounts): quiet 401 for signed-out fans; the
    // resolved session re-renders the chip via FAN_ACCOUNT_EVENT. Customer
    // pages only — DJ-only pages never pay for the request.
    ensureFanSession();
    return () => {
      window.removeEventListener(FAN_PROFILE_EVENT, bump);
      window.removeEventListener(FAN_ACCOUNT_EVENT, bump);
      window.removeEventListener("storage", bump);
    };
  }, [hideAllLinks]);
  const fanAccount = hideAllLinks ? getFanUser() : null;
  const fanSignedIn = hideAllLinks && isFanSignedIn();
  // Identity rework (founder decision): the chip shows ACCOUNT identity only —
  // signed-out fans are anonymous (silhouette + green +), whatever an old
  // install's localStorage still holds.
  const fanName = hideAllLinks ? fanAccount?.name || "" : "";
  const fanDot = hideAllLinks ? hasFanNew() : false;

  // ★-points pill BESIDE the chip (founder pick B — was riding the
  // avatar's bottom edge): signed-in fans see their live balance; signed-out
  // fans with device requests see the points those requests are worth in
  // GOLD — sign-in claims them (the account merge attaches the orders, so
  // the number is honest bait). Brand-new fans see ★ 0. Balance cached in
  // fanAccount; refreshed at most 1×/min.
  const [fanPoints, setFanPoints] = useState<number>(() => getFanPoints() ?? 0);
  const [deviceOrders, setDeviceOrders] = useState<number>(() => listFanOrders().length);
  useEffect(() => {
    if (!hideAllLinks) return;
    const sync = () => setFanPoints(getFanPoints() ?? 0);
    const syncDevice = () => setDeviceOrders(listFanOrders().length);
    window.addEventListener("rq-fan-points", sync);
    window.addEventListener(FAN_PROFILE_EVENT, syncDevice);
    if (isFanSignedIn()) ensureFanPointsFresh().then(sync);
    return () => {
      window.removeEventListener("rq-fan-points", sync);
      window.removeEventListener(FAN_PROFILE_EVENT, syncDevice);
    };
  }, [hideAllLinks, fanSignedIn]);
  const unclaimedPts = !fanSignedIn ? deviceOrders * POINTS_PER_DEVICE_REQUEST : 0;

  // Punch list (sister-phone test): the signup / auth / finish-setup
  // screens used to offer Login + Sign Up — dead ends for someone already
  // mid-flow. Those routes get ONE Home link to the landing page instead.
  // The wordmark keeps its own behavior (logged-in → /requests) on purpose.
  // Sign-up and Log in show NO Home link (build 38, founder): someone there
  // should sign up / sign in, not wander back. The wordmark still goes home,
  // and each page links to the other. Finish-setup, Stripe and the password
  // routes keep their Home link.
  const isSignupRoute = /^\/(register|login)(\/|$)/.test(location.pathname);
  const isGateRoute =
    /^\/(register|login|finish-stripe|stripe-rejected|forgot-password|reset-password)(\/|$)/.test(
      location.pathname
    );

  return (
    <>
    <header ref={headerRef} className="sncf-header">
      <div className="sncf-brand">
        <Link
          to={isAuthenticated ? "/requests" : "/"}
          className="sncf-logo-link"
        >
          <img
            ref={logoRef}
            src="/request-wordmark.png"
            width={807}
            height={247}
            alt={tr("header.requestApp")}
            className="sncf-logo-wordmark"
          />
        </Link>
      </div>

      {hideAllLinks && (
        <div className="sncf-header-actions">
          {/* Fan profile chip — THE Create-Profile entry on every fan page
              (locked r6): gradient bubble + soft silhouette + green "+"
              (top-right) when brand new, their letter once they have a name,
              their photo once signed in (badge gone forever). The resolved
              dot supersedes the badge. Avatar ONLY — the nickname next to
              it ate too much header room (founder 8/25); their name lives
              one tap away in the sheet. Always opens the requests sheet —
              sign-in lives behind its "My Profile ›" row. */}
          <button
            type="button"
            className="fanchip"
            onClick={() => setProfileOpen(true)}
            aria-label={tr("header.myRequests")}
          >
            <span
              className={`fanchip-pts${!fanSignedIn && unclaimedPts > 0 ? " fanchip-pts--gold" : ""}`}
              aria-hidden="true"
            >
              <svg width="8" height="8" viewBox="0 0 24 24" fill="#ffd76a">
                <path d="m12 2.5 2.9 6 6.6.9-4.8 4.6 1.2 6.5L12 17.4l-5.9 3.1 1.2-6.5L2.5 9.4l6.6-.9Z" />
              </svg>
              {fanSignedIn ? fanPoints : unclaimedPts}
            </span>
            <FanAvatar
              className="fanchip-avatar"
              name={fanName}
              avatarUrl={fanAccount?.avatarUrl}
              showBadge={!fanSignedIn && !fanDot}
              silhouetteSize={18}
            >
              {fanDot && <span className="fanchip-dot" aria-hidden="true" />}
            </FanAvatar>
          </button>
        </div>
      )}

      {!hideAllLinks && (
        <div className="sncf-header-actions">
          {isSignupRoute ? null : isGateRoute ? (
            <Link to="/" className="sncf-home-link">
              {tr("header.home")}
            </Link>
          ) : isAuthenticated ? (
            <>
              {/* Desktop nav — hidden on phones (the bottom tab bar covers it). */}
              <nav className="sncf-desktop-nav">
                <Link to="/requests" className="sncf-requests-link">
                  {tr("header.requests")}
                  {pendingCount > 0 && (
                    <span className="sncf-badge">{pendingCount}</span>
                  )}
                </Link>
                {/* Balance doorway for desktop web (>=769px hides the bottom
                    tab bar). Mobile + the Mac Companion reach Balance via the
                    tab bar; without this, desktop-web DJs would have no path to
                    Balance / Transfer-to-Bank after the tab redesign. */}
                {/* data-money-target: landing pad for the queued-card $ flight
                    on desktop web, where the tab bar is hidden (flyToBalance). */}
                <Link to="/balance" data-money-target="balance">{tr("header.balance")}</Link>
                {/* Hash-only link: opens the Profile drawer over whatever page
                    is showing (Profile is no longer a route, 2026-08-06). */}
                <Link to="#profile">{tr("header.myProfile")}</Link>
                <Link to={`/qr/${user?.slug}`}>{tr("header.myQr")}</Link>
                <button onClick={handleLogout} className="sncf-btn-logout">
                  {tr("header.logout")}
                </button>
              </nav>
            </>
          ) : (
            // Unauthenticated: shown on ALL sizes (there's no burger anymore and
            // the home page has no login link, so this is the only way a
            // returning talent signs in on mobile).
            <nav className="sncf-auth-nav">
              {/* The public leaderboard (1.5.2): open to every visitor. */}
              <Link
                ref={topSongsRef}
                to="/fan/leaderboard"
                className="sncf-top-songs"
                aria-label={tr("dJLeaderboard.topSongs")}
              >
                {/* Shown only when the words don't fit (Header.css). */}
                <svg className="sncf-top-songs-ico" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="M8 4h8v6a4 4 0 0 1-8 0V4Z" />
                  <path d="M8 6H5.5a.5.5 0 0 0-.5.5V7a3 3 0 0 0 3 3" />
                  <path d="M16 6h2.5a.5.5 0 0 1 .5.5V7a3 3 0 0 1-3 3" />
                  <path d="M12 14v4" />
                  <path d="M8.5 20h7" />
                </svg>
                <span className="sncf-top-songs-text">{tr("dJLeaderboard.topSongs")}</span>
              </Link>
              <Link to="/login" className="sncf-auth-login">
                {tr("header.login")}
              </Link>
              <Link to="/register" className="sncf-btn-signup">
                {tr("header.signUp")}
              </Link>
            </nav>
          )}
        </div>
      )}
    </header>
    {hideAllLinks && (
      <>
        <FanProfileSheet
          open={profileOpen}
          onClose={() => setProfileOpen(false)}
          onOpenRefer={() => setReferOpen(true)}
          onSignedIn={afterSignIn}
          onOpenEditor={() => {
            // Identity = account-only (founder decision): signed-out fans have
            // nothing to edit — "My Profile ›" IS the sign-up door for them.
            if (isFanSignedIn()) setEditorOpen(true);
            else setSignInOpen(true);
          }}
          onVerifyInstagram={() => {
            setEditorVerify(true);
            setEditorOpen(true);
          }}
        />
        {/* Mounted after the profile sheet so they stack ON TOP when opened
            from its rows — closing either drops the fan back into their
            profile. */}
        <FanProfileEditorSheet
          open={editorOpen}
          connectOnOpen={editorVerify}
          onClose={() => {
            setEditorOpen(false);
            setEditorVerify(false);
          }}
        />
        <FanReferEarnSheet open={referOpen} onClose={() => setReferOpen(false)} />
        <FanSignInSheet open={signInOpen} onClose={() => setSignInOpen(false)} onSignedIn={afterSignIn} />
      </>
    )}
    </>
  );
};

export default Header;
